import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn'

interface ModalProps { open: boolean; onClose: () => void; title?: string; children: ReactNode; className?: string }
const focusable = 'button,input,select,textarea,a[href],[tabindex]'

/** Accessible warm-paper dialog with a scrollable mobile surface. */
export function Modal({ open, onClose, title, children, className }: ModalProps) {
  const dialog = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose
  const headingId = useId()
  useEffect(() => {
    if (!open || !dialog.current) return
    const node = dialog.current
    const trigger = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    const siblings = Array.from(document.body.children).filter(el => !el.contains(node)) as HTMLElement[]
    const originals = siblings.map(el => ({ el, inert: el.inert, hidden: el.getAttribute('aria-hidden') }))
    for (const { el } of originals) { el.inert = true; el.setAttribute('aria-hidden', 'true') }
    document.body.style.overflow = 'hidden'
    const controls = () => Array.from(node.querySelectorAll<HTMLElement>(focusable)).filter(el => !el.matches(':disabled') && el.tabIndex >= 0 && !el.hidden && el.getAttribute('aria-hidden') !== 'true' && !el.closest('[hidden]')).sort((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1)
    const first = () => controls()[0] ?? node
    first().focus()
    const onFocus = (event: FocusEvent) => { if (!node.contains(event.target as Node)) first().focus() }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close.current(); return }
      if (event.key !== 'Tab') return
      const items = controls(), last = items.at(-1)
      if (!items.length) { event.preventDefault(); node.focus() }
      else if (event.shiftKey && (document.activeElement === items[0] || document.activeElement === node)) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); items[0].focus() }
    }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('focusin', onFocus)
    return () => {
      document.removeEventListener('keydown', onKey, true); document.removeEventListener('focusin', onFocus)
      document.body.style.overflow = overflow
      for (const { el, inert, hidden } of originals) { el.inert = inert; if (hidden === null) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden', hidden) }
      if (trigger?.isConnected) trigger.focus()
    }
  }, [open])
  if (!open) return null
  return createPortal(<div className="fixed inset-0 z-50 flex items-center justify-center px-3 py-4 sm:px-4 sm:py-8" style={{ background: 'rgba(36,31,26,.42)' }} onClick={onClose}>
    <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={title ? headingId : undefined} aria-label={title ? undefined : 'Dialog'} className={cn('w-full max-w-[520px] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-[14px] border border-line bg-paper-surface shadow-modal animate-rise-modal', className)} onClick={e => e.stopPropagation()}>
      {title && <div className="border-b border-line-row px-5 pt-5 pb-4 sm:px-7"><h2 id={headingId} className="font-serif text-[26px] font-medium leading-tight text-ink">{title}</h2></div>}
      <div className="px-5 py-5 sm:px-7 sm:py-6">{children}</div>
    </div>
  </div>, document.body)
}
